import { useAntdTable, useDebounceFn } from 'ahooks'
import { Col, DatePicker, Form, Input, Row, Select, Table, TableColumnsType, TableProps, Typography } from 'antd'
import { forIn, isArray } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory, useLocation } from 'react-router-dom'

import {
  MosaicComponent,
  ProjectProgress,
  RiskBar,
  UserAvatar,
  UsersSearch,
  getPointPermission,
  statusOptions,
} from '@/components'
import { classificationOptions } from '@/modules/project/project-create/project-form.component'
import { routes } from '@/router'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, Project, ProjectClassification, ProjectStatus } from '@/schemas'
import { traceEvent } from '@/utils/ga'

import { HotResource } from './hot-resource/hot-resource.component'

interface ProjectListProps extends TableProps<any> {
  renderExtras?: any
  isReplaceRouter?: boolean
  extraOperationLoading?: boolean
}

const searchType = [
  {
    label: 'all',
    value: 0,
  },
  {
    label: 'owner',
    value: 1,
  },
  {
    label: 'join in',
    value: 2,
  },
]

export const ProjectList: React.FC<ProjectListProps> = ({
  renderExtras,
  isReplaceRouter = false,
  extraOperationLoading = false,
  ...extraTableProps
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const history = useHistory()
  const location = useLocation()
  const searchParsed = queryString.parse(location.search)

  const defaultParams = {}
  forIn(searchParsed, (value, key) => {
    switch (key) {
      case 'searchType':
        defaultParams['searchType'] = Number(value)
        break
      case 'statuses':
        if (isArray(value)) {
          defaultParams['statuses'] = value.map((s) => Number(s))
        } else {
          defaultParams['statuses'] = [Number(value)]
        }
        break
      case 'startDate':
        if (searchParsed['startDate'] && searchParsed['endDate']) {
          if (defaultParams['date']) {
            defaultParams['date'][0] = moment(value)
          } else {
            defaultParams['date'] = [moment(value), undefined]
          }
        }
        break
      case 'classification':
        if (value) {
          defaultParams['classification'] = Number(value)
        }
        break
      case 'endDate':
        if (defaultParams['date']) {
          defaultParams['date'][1] = moment(value)
        } else {
          defaultParams['date'] = [undefined, moment(value)]
        }
        break
      case 'page':
        defaultParams['current'] = Number(value)
        break
      case 'size':
        defaultParams['pageSize'] = Number(value)
        break
      case 'orderBy':
        defaultParams['order'] = value === 'asc' ? 'ascend' : value
        break
      case 'sortBy':
        defaultParams['columnKey'] = value
        break
      case 'priorities':
        if (isArray(value)) {
          defaultParams['priority'] = value.map((s) => Number(s))
        } else {
          defaultParams['priority'] = Number(value)
        }
        break

      default:
        defaultParams[key] = value
    }
  })
  if (!defaultParams['statuses']) {
    defaultParams['statuses'] = [0, 1, 3, 4]
  }
  if (!defaultParams['searchType']) {
    defaultParams['searchType'] = 1
  }

  const { tableProps, search } = useAntdTable<{ all: Project[]; list: Project[]; total: number }>(
    ({ current, pageSize, sorter, filters }, params) => {
      isReplaceRouter &&
        history.replace({
          search: queryString.stringify({
            page: current,
            size: pageSize,
            name: params.name || null,
            statuses: params.statuses,
            classification: params.classification,
            startDate: params.date ? params.date[0].format('YYYY-MM-DD') : undefined,
            endDate: params.date ? params.date[1].format('YYYY-MM-DD') : undefined,
            email: params.email,
            searchType: params.searchType,
            proportion: params.proportion,
            ...(filters && filters.priority && { priorities: filters.priority }),
            sortBy: sorter?.columnKey,
            orderBy: sorter && sorter.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : undefined,
          }),
        })
      return {
        url: '/api/project/getProjects',
        params: {
          page: current,
          size: pageSize,
          name: params.name || null,
          statuses: params.statuses,
          classification: params.classification,
          startDate: params.date ? params.date[0].format('YYYY-MM-DD') : undefined,
          endDate: params.date ? params.date[1].format('YYYY-MM-DD') : undefined,
          email: params.email,
          searchType: params.searchType,
          proportion: params.proportion,
          ...(filters && filters.priority && { priorities: filters.priority }),
          sortBy: sorter?.columnKey,
          orderBy: sorter && sorter.order ? (sorter.order === 'ascend' ? 'asc' : 'desc') : undefined,
          download: params.download,
        },
      }
    },
    {
      form,
      defaultParams: [
        {
          current: defaultParams['current'] || 1,
          pageSize: defaultParams['pageSize'] || 10,
          sorter: {
            order: defaultParams['order'] || undefined,
            columnKey: defaultParams['columnKey'] || undefined,
          },
        },
        defaultParams,
      ],
      defaultPageSize: 10,
    }
  )

  const { run: runSearch } = useDebounceFn(search.submit, {
    wait: 500,
  })

  const columns: TableColumnsType<Project> = [
    {
      title: t('project.owner'),
      width: 150,
      render: (_, d) => {
        return (
          <div className='flex items-stretch'>
            <RiskBar risk={d.risk} />
            {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_负责人, d.permissions).canRead ? (
              <MosaicComponent />
            ) : (
              <UserAvatar showName email={d.email} />
            )}
          </div>
        )
      },
    },
    {
      title: t('project.name'),
      width: 200,
      render: (_, d) => (
        <div className='leading-snug'>
          <div>
            <Link
              to={routes.project.page(d.selfId)}
              onClick={() => {
                window.sessionStorage.setItem('entry', traceEvent.detailPage.event_categories.ef_pmo_project)
              }}
            >
              <HotResource selfId={d.selfId} type={d.type} readonly hotData={d.hots} />
              <span className='ml-4'>{d.name}</span>
            </Link>
          </div>
          <div>
            <Typography.Text className='text-xs' type='secondary'>
              {d.classification ? t(ProjectClassification[d.classification]) : null}
            </Typography.Text>
          </div>
        </div>
      ),
    },
    {
      title: t('devlop.detail.priority'),
      width: 100,
      align: 'center',
      filters: Object.keys(PriorityEnum).map((key) => ({
        text: t(`form.priority.${key}`),
        value: PriorityEnum[key],
      })),
      sorter: true,
      key: 'priority',
      responsive: ['md'],
      filterMultiple: true,
      render: (_, d) =>
        !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_优先级, d.permissions).canRead ? (
          <MosaicComponent />
        ) : d.priority ? (
          t(`form.priority.${PriorityEnum[d.priority]}`)
        ) : null,
    },
    {
      title: t('status'),
      width: 120,
      align: 'center',
      sorter: true,
      key: 'status',
      render: (_, d) =>
        !getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, d.permissions).canRead ? (
          <MosaicComponent />
        ) : d.status ? (
          t(ProjectStatus[d.status])
        ) : null,
    },
    {
      title: t('progress'),
      width: 80,
      sorter: true,
      key: 'progress',
      align: 'center',
      render: (_, d) => <ProjectProgress project={d} />,
    },
    {
      title: t('startend'),
      width: 200,
      align: 'center',
      sorter: true,
      key: 'startDate',
      render: (_, d) => (
        <div className='flex text-sm'>
          {!getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, d.permissions).canRead ? (
            <MosaicComponent />
          ) : (
            <>
              <div className='w-80px'>{d.startDate && moment(d.startDate).format('l')}</div>
              <div className='px-4px'>-</div>
              <div className='w-80px'>{d.endDate && moment(d.endDate).format('l')}</div>
            </>
          )}
        </div>
      ),
    },
  ]

  if (renderExtras) {
    columns.push({
      align: 'center',
      width: 80,
      render: renderExtras,
    })
  }

  return (
    <>
      <Form
        form={form}
        onValuesChange={() => {
          runSearch()
        }}
      >
        <Row gutter={[30, 0]}>
          <Col xs={6} lg={4}>
            <Form.Item name='name'>
              <Input allowClear placeholder={t('project.name')} />
            </Form.Item>
          </Col>
          <Col xs={6} lg={8}>
            <Input.Group compact className='!flex'>
              <Form.Item name='email' className='flex-1'>
                <UsersSearch placeholder={t('project.owner')} />
              </Form.Item>
              <Form.Item name='searchType' className='w-100px'>
                <Select
                  options={searchType.map((s) => {
                    return {
                      label: t(s.label),
                      value: s.value,
                    }
                  })}
                />
              </Form.Item>
            </Input.Group>
          </Col>
          <Col xs={6} lg={8}>
            <Form.Item name='statuses'>
              <Select
                allowClear
                mode='tags'
                placeholder={t('status')}
                options={statusOptions.map((s) => {
                  return {
                    label: t(s.label),
                    value: s.value,
                  }
                })}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[30, 0]}>
          <Col xs={15} lg={10}>
            <Form.Item name='classification'>
              <Select
                allowClear
                placeholder={t('devlop.detail.classification')}
                options={classificationOptions}
              ></Select>
            </Form.Item>
          </Col>
          <Col xs={15} lg={10}>
            <Form.Item name='date'>
              <DatePicker.RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        rowKey='selfId'
        size='middle'
        columns={columns}
        {...tableProps}
        {...extraTableProps}
        loading={extraOperationLoading || tableProps.loading}
        // @ts-ignore
        pagination={{
          ...tableProps.pagination,
          showTotal: (total) => `${total} 个项目`,
          hideOnSinglePage: true,
        }}
      />
    </>
  )
}
